import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import { listingIdOf } from '../../util/featured';
import { favoriteEntityId, isFavoriteListing, isFavoriteProfile } from '../../util/favorites';
import { isFavoritesTalkerMode } from '../../util/siteNavMode';
import { toggleFavoriteListing, toggleFavoriteProfile } from '../../ducks/user.duck';

import css from './FavoriteButton.module.css';
import BookmarkIcon from '../BookmarkIcon/BookmarkIcon';

/**
 * Signed-in heart. Listing hearts store the listing id (Listening profile).
 * Profile hearts store the user id (Talking profile). Hidden for guests and
 * for the signed-in person's own listing or /u page.
 *
 * @param {Object} props
 * @param {string} [props.listingId]
 * @param {object} [props.listing]
 * @param {object} [props.user]
 * @param {'listing'|'profile'} [props.target]
 * @param {string} [props.className]
 * @returns {JSX.Element|null}
 */
const FavoriteButton = props => {
  const { listingId, listing, user, target = 'listing', className, showLabel = false } = props;
  const intl = useIntl();
  const dispatch = useDispatch();
  const currentUser = useSelector(state => state.user?.currentUser);
  const currentUserHasListings = useSelector(state => !!state.user?.currentUserHasListings);
  const [inProgress, setInProgress] = useState(false);

  const currentUserId = favoriteEntityId(currentUser);
  const isProfile = target === 'profile';
  const storedId = isProfile
    ? favoriteEntityId(user) || favoriteEntityId(listingId)
    : listingIdOf(listing) || favoriteEntityId(listingId);
  const ownerId = isProfile ? favoriteEntityId(user) : favoriteEntityId(listing?.author);
  const isOwn = !!(currentUserId && ownerId && currentUserId === ownerId);
  const talkerMode = isFavoritesTalkerMode({ currentUser, currentUserHasListings });
  if (!currentUserId && storedId) {
    // Logged out: show the heart; tapping it asks them to sign up (v715).
    return (
      <a
        href="/signup?role=talk&fav=1"
        className={classNames(css.root, className, { [css.withLabel]: showLabel })}
        aria-label="Sign up to save"
        title="Sign up to save"
        data-favorite="signup"
        onClick={e => e.stopPropagation()}
      >
        <BookmarkIcon className={css.heart} on={false} />
        {showLabel ? (
          <span className={css.label} aria-hidden="true" data-favorite-label="true">
            {intl.formatMessage({ id: 'FavoriteButton.favorite' })}
          </span>
        ) : null}
      </a>
    );
  }
  if (!currentUserId || !storedId || isOwn || !talkerMode) {
    return null;
  }

  const favorited = isProfile
    ? isFavoriteProfile(currentUser, storedId)
    : isFavoriteListing(currentUser, storedId);
  const label = intl.formatMessage({
    id: favorited ? 'FavoriteButton.unfavorite' : 'FavoriteButton.favorite',
  });

  const onClick = event => {
    event.preventDefault();
    event.stopPropagation();
    if (inProgress) {
      return;
    }
    setInProgress(true);
    const action = isProfile ? toggleFavoriteProfile(storedId) : toggleFavoriteListing(storedId);
    Promise.resolve(dispatch(action)).finally(() => {
      setInProgress(false);
    });
  };

  return (
    <button
      type="button"
      className={classNames(css.root, className, { [css.on]: favorited, [css.withLabel]: showLabel })}
      aria-pressed={favorited}
      aria-label={label}
      title={label}
      data-favorite={favorited ? 'on' : 'off'}
      data-favorite-target={isProfile ? 'profile' : 'listing'}
      data-favorite-id={storedId}
      disabled={inProgress}
      onClick={onClick}
    >
      <BookmarkIcon className={css.heart} on={favorited} />
      {showLabel ? (
        <span className={css.label} aria-hidden="true" data-favorite-label="true">
          {label}
        </span>
      ) : null}
    </button>
  );
};

export default FavoriteButton;
